import React, { useState, useEffect } from 'react';
import { Play, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { parseVideoUrl, generateThumbnailUrl, generateEmbedUrl } from '@/utils/videoUtils';
import { cn } from '@/lib/utils';

interface VideoPlayerProps {
  videoUrl: string;
  title: string;
  imageUrl?: string;
  className?: string;
  aspectRatio?: 'video' | 'square';
  autoplay?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  title,
  imageUrl,
  className,
  aspectRatio = 'video',
  autoplay = false
}) => {
  const [videoInfo, setVideoInfo] = useState<{ platform: 'youtube' | 'vimeo'; videoId: string; embedUrl: string; thumbnailUrl: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // Process video URL when component mounts or URL changes
  useEffect(() => {
    if (!videoUrl) {
      setLoading(false);
      setVideoInfo(null);
      return;
    }

    setLoading(true);
    setError(null);
    setIframeError(false);
    
    try {
      console.log('VideoPlayer: Processing video URL:', videoUrl);
      const parsed = parseVideoUrl(videoUrl);
      
      if (!parsed) {
        setError('Unsupported video URL format');
        setLoading(false);
        return;
      }
      
      const { platform, videoId } = parsed;
      const embedUrl = generateEmbedUrl(platform, videoId);
      const thumbnailUrl = generateThumbnailUrl(platform, videoId);
      
      const info = {
        platform,
        videoId,
        embedUrl,
        thumbnailUrl
      };
      
      console.log('VideoPlayer: Processed video info:', info);
      setVideoInfo(info);
      
      if (!embedUrl) {
        console.warn('VideoPlayer: No embed URL generated for:', info);
        setError(`Unsupported video format: ${platform}`);
      }
    } catch (err) {
      console.error('VideoPlayer: Error processing video URL:', err);
      setError('Failed to process video URL');
    } finally {
      setLoading(false);
    }
  }, [videoUrl]);

  // Auto-show video if autoplay is enabled and we have a valid embed URL
  useEffect(() => {
    if (autoplay && videoInfo?.embedUrl && !error) {
      setShowVideo(true);
    }
  }, [autoplay, videoInfo, error]);

  const handlePlayClick = () => {
    setShowVideo(true);
    setIframeError(false);
  };

  const handleRetry = () => {
    setError(null);
    setIframeError(false);
    setShowVideo(false);
    
    // Re-process the video URL
    if (videoUrl) {
      const parsed = parseVideoUrl(videoUrl);
      if (parsed) {
        const { platform, videoId } = parsed;
        const embedUrl = generateEmbedUrl(platform, videoId);
        const thumbnailUrl = generateThumbnailUrl(platform, videoId);
        
        const info = {
          platform,
          videoId,
          embedUrl,
          thumbnailUrl
        };
        
        setVideoInfo(info);
        if (embedUrl) {
          setShowVideo(true);
        }
      }
    }
  };

  const handleIframeError = () => {
    console.error('Iframe failed to load video:', videoInfo?.embedUrl);
    setIframeError(true);
  };

  // Determine what to show as background/fallback
  const getThumbnailUrl = (): string | null => {
    // Priority: video thumbnail > provided image > null
    if (videoInfo?.thumbnailUrl) {
      return videoInfo.thumbnailUrl;
    }
    // Check for valid image URL (not empty string or just whitespace)
    if (imageUrl && imageUrl.trim() !== '') {
      return imageUrl;
    }
    return null;
  };

  const thumbnailUrl = getThumbnailUrl();
  const aspectRatioClass = aspectRatio === 'square' ? 'pb-[100%]' : 'pb-[56.25%]';

  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "relative w-full h-0 overflow-hidden rounded-lg bg-muted",
        aspectRatioClass,
        className
      )}>
        <div className="absolute inset-0 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Error state or unsupported video
  if (error || !videoInfo?.embedUrl) {
    return (
      <div className={cn(
        "relative w-full h-0 overflow-hidden rounded-lg",
        aspectRatioClass,
        className
      )}>
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/sequence-cover-placeholder.svg';
            }}
          />
        ) : (
          // User requested no placeholder when both video and album artwork are missing
          <div className="absolute inset-0 bg-muted"></div>
        )}
        
        {error && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
            <Alert className="max-w-sm bg-background/90 backdrop-blur-xs">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {error}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="mt-2 w-full"
                >
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
    );
  }

  // Show video player or thumbnail with play button
  return (
    <div className={cn(
      "relative w-full h-0 overflow-hidden rounded-lg bg-black",
      aspectRatioClass,
      className
    )}>
      {showVideo && !iframeError ? (
        <iframe
          className="absolute inset-0 w-full h-full border-0"
          src={videoInfo.embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onError={handleIframeError}
          onLoad={() => {
            // Iframe loaded successfully
            console.log('Video iframe loaded successfully:', videoInfo.platform);
          }}
        />
      ) : (
        <>
          {/* Thumbnail background */}
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = '/sequence-cover-placeholder.svg';
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">Video Preview</span>
            </div>
          )}
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
            <Button
              variant="secondary"
              size="lg"
              onClick={handlePlayClick}
              className="rounded-full bg-white/90 hover:bg-white text-black shadow-lg"
            >
              <Play className="w-6 h-6 ml-1" fill="currentColor" />
            </Button>
          </div>

          {/* Platform badge */}
          {videoInfo.platform && (
            <div className="absolute top-3 right-3">
              <span className="bg-black/70 text-white text-xs px-2 py-1 rounded capitalize">
                {videoInfo.platform}
              </span>
            </div>
          )}
        </>
      )}

      {/* Error overlay for iframe failures */}
      {iframeError && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
          <Alert className="max-w-sm bg-background/90 backdrop-blur-xs">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Video failed to load. This may be due to platform restrictions.
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="flex-1"
                >
                  Retry
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(videoUrl, '_blank')}
                  className="flex-1"
                >
                  Open Original
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
};